// Mirrors the backend Pydantic shapes in maisight/backend/parse/types.py.
// Forward-compatible per invariant #6 — unknown fields are ignored by the renderer.

export type Format = "markdown" | "mdx" | "openapi" | "latex";

export type TermKind =
  | "concept"
  | "acronym"
  | "type"
  | "error"
  | "param"
  | "resource"
  | "other";

export type TermOrigin = "openapi" | "user" | "llm" | "extension";

export interface InlineRun {
  kind: "text" | "code" | "math" | "link" | "emph" | "strong" | "cite";
  text?: string;
  href?: string;
  meta?: Record<string, unknown>;
}

export type BlockKind =
  | "paragraph"
  | "list"
  | "code"
  | "blockquote"
  | "table"
  | "equation"
  | "figure"
  | "callout"
  | "mdx_component"
  | "openapi_resource";

export interface Block {
  id: string;
  kind: BlockKind;
  inline?: InlineRun[];
  raw?: string;
  meta?: Record<string, unknown>;
}

export interface Section {
  id: string;
  level: number;
  heading?: string;
  blocks: Block[];
}

export interface DocJSON {
  doc_id: string;
  pipeline_version: number;
  format: Format;
  title?: string;
  sections: Section[];
  citations: Citation[];
  // 00_processor §Corpus entity — additive in v1.2. Renderer falls back
  // to "default" when absent.
  corpus_id?: string;
}

// 00_processor §Reader register — two-register definition + prereq DAG.
// Additive in v1.2; readers prefer this when present, fall back to the
// bare `definition` string otherwise (forward-compat invariant #6).
export interface Definition {
  definition_plain: string;
  definition_precise: string;
  prerequisites: string[];
  analogy?: string | null;
}

export interface Citation {
  id: string;
  raw: string;
  resolved_to?: string | null;
}

export interface GlossaryEntry {
  term_id: string;
  term: string;
  alternates: string[];
  definition: string;
  // v1.2 additive — present once extractor swap lands; readers prefer
  // this and fall back to wrapping `definition` as the precise register.
  definition_obj?: Definition | null;
  kind: TermKind;
  origin: TermOrigin;
  source_block_id?: string | null;
  source_offset?: number | null;
  source_length?: number | null;
  is_singleton?: boolean;
  confidence?: number | null;
  // Visual taxonomy. Decoupled from `kind`. Open string for
  // extensibility. See frontend/src/reading/termFamily.ts.
  term_family?: string | null;
  // PRD 09 — compound-term constituents. List of term_ids of other
  // entries that are subspans of this compound AND carry independent
  // meaning. Drives the "Built from:" disclosure and constituent
  // sub-span highlighting. Empty / missing on non-compound entries.
  includes?: string[];
}

// === Pass 2 canonical entities (00_processor §Pass 2 data model).
// Mirrors backend/corpus/entities.py. Optional in payloads — Pass 2 may
// never have run for a given corpus (cold start). ===

export type EntityKind = "term" | "symbol";

export interface CorpusEntity {
  entity_id: string;
  corpus_id: string;
  kind: EntityKind;
  name: string;
  alternates: string[];
  definition: Definition;
  /** "<doc_id>:<term_id-or-symbol_id>" */
  member_refs: string[];
  structural: Record<string, unknown>;
  confidence: number | null;
  is_orphan: boolean;
  last_reconciled_at: string;
}

export interface CorpusGlossary {
  corpus_id: string;
  reconcile_version: number;
  entities: CorpusEntity[];
  prereq_dag: Record<string, string[]>;
  embedded_at: string;
}

// === Equations (PRD 07 § 5). Mirrors backend/parse/types.py. Additive
// per invariant #6 — readers ignore unknown fields, older docs lack
// these keys entirely. ===

export type AstKind =
  | "expression"
  | "equality"
  | "definition"
  | "inequality"
  | "system"
  | "matrix"
  | "unknown";

export type SymbolRole =
  | "variable"
  | "function"
  | "operator"
  | "constant"
  | "index"
  | "tensor"
  | "set"
  | "distribution"
  | "other";

export type SymbolGrounding =
  | "where_clause"
  | "prior_equation"
  | "domain_convention"
  | "ungrounded";

export interface EquationSymbol {
  symbol_id: string;
  latex: string;
  role?: SymbolRole | null;
  definition?: Definition | null;
  units?: string | null;
  depends_on?: string[];
  grounding?: SymbolGrounding | null;
  grounding_ref?: string | null;
  corpus_symbol_id?: string | null;
  confidence?: number | null;
}

export interface EquationEntry {
  equation_id: string;
  block_id: string;
  latex: string;
  ast_kind: AstKind;
  presentation?: "display" | "inline";
  summary?: Definition | null;
  interactions?: string | null;
  symbols: EquationSymbol[];
  pedagogy_ref?: string | null;
  is_singleton?: boolean;
  confidence?: number | null;
  // PRD 10 Phase C — glossary↔equation bridge. term_ids of glossary
  // entries whose definition contains LaTeX matching this equation.
  // Renderer surfaces as data-glossary-refs on the EquationBlock root.
  // Forward-compat additive: optional, default absent.
  glossary_refs?: string[];
}

export interface ProposedEquations {
  doc_id: string;
  corpus_id?: string | null;
  extraction_equation_version: number;
  extracted_at: string;
  truncated: boolean;
  equations: EquationEntry[];
}

export interface PublishedReviewState {
  action: "accepted" | "edited" | "added";
  actor: string;
  at: string;
  original_definition?: string | null;
}

// review_state is optional in the TS interface so older fixtures
// (slice-1 demo) and pre-review payloads remain assignable. The backend
// always emits it after publish; the renderer treats absence as
// "accepted, no edit" per invariant #6 (forward-compatible JSON).
export interface PublishedTerm extends GlossaryEntry {
  review_state?: PublishedReviewState;
}

export interface PublishedGlossary {
  doc_id: string;
  extraction_glossary_version: number;
  published_version: number;
  published_at: string;
  published_by: string;
  terms: PublishedTerm[];
}

// === Review-flow types (consumed by frontend/src/review/, mirror the
// backend Pydantic shapes in maisight/backend/api/routes/review.py) ===

export type ReviewAction = "accepted" | "edited" | "rejected" | "pending" | "added";

export interface TermReviewAction {
  action: ReviewAction;
  actor: string;
  at: string;
  edited_definition?: string | null;
  edited_kind?: TermKind | null;
  edited_alternates?: string[] | null;
}

export interface ProposedGlossary {
  doc_id: string;
  extraction_glossary_version: number;
  terms: GlossaryEntry[];
}

export interface ReviewStatePayload {
  doc_id: string;
  extraction_glossary_version: number;
  actions: Record<string, TermReviewAction>;
  added: GlossaryEntry[];
  saved_at: string;
}

export interface ReviewResponse {
  doc: DocJSON;
  glossary_proposed: ProposedGlossary;
  review_state?: ReviewStatePayload | null;
}

export interface PublishResponse {
  doc_id: string;
  published_at: string;
  published_version: number;
}

// === Per-corpus appearance + layout config (PRD 08 § 5). Mirrors the
// backend Pydantic shape in maisight/backend/corpus/config.py. ===

export type AccentSlug = "rust" | "teal" | "violet" | "forest" | "slate";
export type ThemeMode = "light" | "dark" | "auto";
export type TypeScale = "small" | "medium" | "large";
export type RegisterDefault = "plain" | "precise";
export type RailPosition = "left" | "right" | "split";

export interface CorpusConfig {
  schema_version: number;
  theme: ThemeMode;
  accent: AccentSlug;
  type_scale: TypeScale;
  default_register: RegisterDefault;
  rail_position: RailPosition;
}

export const CORPUS_CONFIG_DEFAULTS: CorpusConfig = {
  schema_version: 1,
  theme: "auto",
  accent: "rust",
  type_scale: "medium",
  default_register: "plain",
  rail_position: "right",
};
